import React, { useState, useEffect } from 'react';
import { Alert, Button, message, Modal, Spin, Switch } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import s from './s.module.less';
import { IAdBudgetDisplay, BudgetStatus, BudgetPeriod } from 'types/marketing';
import StrategyTable from 'pages/ProListingSetup/components/StrategyTable';
import SpecialtyBidOverrides from 'pages/ProListingSetup/components/SpecialtyBidOverrides';
import { updateAdBudgetByOps } from 'api/marketing';
import { convertBudgetToSave } from 'utils/marketing';
import useOpsUserByProvider from 'hooks/useOpsUserByProvider';

const GP_PROVIDERS_PATH = '/operation/hello-klarity-providers?type=gp';

interface ProListingModalProps {
    visible: boolean;
    mode: 'add' | 'edit';
    initialData?: IAdBudgetDisplay;
    onCancel: () => void;
    onSaveSuccess?: () => void;
    providerId: number;
    hasJoinedAdSystem: boolean;
}

const ProListingModal: React.FC<ProListingModalProps> = ({
    visible,
    mode,
    initialData = {
        status: BudgetStatus.ACTIVE,
        generalBidAmount: 40,
        budgetPeriod: BudgetPeriod.WEEKLY,
        budgetCap: 200,
        specialtyBids: [],
    },
    onCancel,
    providerId,
    onSaveSuccess,
    hasJoinedAdSystem,
}) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [strategyData, setStrategyData] = useState<IAdBudgetDisplay>(initialData);

    // Resolve the provider's user only while the modal is open. If they belong to a group practice,
    // their Pro-Listings are managed at the group level, so editing here is not allowed.
    const [opsUser, opsUserLoading] = useOpsUserByProvider(visible ? providerId : undefined);
    const isTeamMember = !!opsUser?.groupPractice;

    const goToGroupPractices = () => {
        onCancel();
        navigate(GP_PROVIDERS_PATH);
    };

    const renderBody = () => {
        if (opsUserLoading) {
            return (
                <div className="flex justify-center py-10">
                    <Spin />
                </div>
            );
        }
        return (
            <div className={s.modalContent}>
                {isTeamMember && (
                    <Alert
                        className={`${s.gpHint} mb-4`}
                        type="warning"
                        icon={<WarningFilled />}
                        showIcon
                        message="This provider is a team member of a group practice — their Pro-Listings are managed at the group level."
                        action={
                            <Button type="link" className={s.gpHintAction} onClick={goToGroupPractices}>
                                Go to Group Practices →
                            </Button>
                        }
                    />
                )}
                <div className="mb-4">
                    <div className="mb-1 font-medium">Status</div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={strategyData.status === BudgetStatus.ACTIVE}
                            onChange={(checked) =>
                                setStrategyData({
                                    ...strategyData,
                                    status: checked
                                        ? BudgetStatus.ACTIVE
                                        : hasJoinedAdSystem
                                          ? BudgetStatus.PAUSED
                                          : undefined,
                                })
                            }
                        />
                        <div>{strategyData.status === BudgetStatus.ACTIVE ? 'Active' : 'Inactive'}</div>
                    </div>
                </div>
                <StrategyTable
                    data={strategyData}
                    onChange={(data) => setStrategyData({ ...strategyData, ...data })}
                    isOps
                    providerId={providerId}
                />
                <div className="mt-4">
                    <div className="mb-1 font-medium">Advanced settings</div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={strategyData.enableAdvancedSettings}
                            onChange={(checked) =>
                                setStrategyData({ ...strategyData, enableAdvancedSettings: checked })
                            }
                        />
                        <div>{strategyData.enableAdvancedSettings ? 'Active' : 'Inactive'}</div>
                    </div>
                    {strategyData.enableAdvancedSettings && (
                        <SpecialtyBidOverrides
                            bidPerBooking={strategyData.generalBidAmount}
                            budgetCap={strategyData.budgetCap || 0}
                            budgetPeriod={strategyData.budgetPeriod}
                            specialtyBids={strategyData.specialtyBids}
                            onChange={(data) => setStrategyData({ ...strategyData, specialtyBids: data })}
                            isOps
                            providerId={providerId}
                        />
                    )}
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (initialData) {
            setStrategyData(initialData);
        }
    }, [initialData]);

    const handleSave = async () => {
        if (!strategyData.status) {
            onCancel();
            return;
        }
        try {
            const result = await updateAdBudgetByOps(providerId, convertBudgetToSave(strategyData));
            if (!result.error) {
                message.success('Saved successfully');
                onCancel();
                onSaveSuccess?.();
            }
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={mode === 'add' ? 'Add a new campaign' : 'Edit campaign'}
            open={visible}
            onCancel={onCancel}
            okText="Save"
            onOk={handleSave}
            okButtonProps={{ loading, style: isTeamMember ? { display: 'none' } : undefined }}
            destroyOnClose
            width={1036}
            style={{
                maxHeight: '90vh',
                overflowY: 'auto',
            }}
        >
            {renderBody()}
        </Modal>
    );
};

export default ProListingModal;
